// ============================================================
// Shared UI primitives — ported from design/prototype/ui.jsx
// ============================================================
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ChangeEvent,
  type CSSProperties,
  type InputHTMLAttributes,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type Ref,
  type SelectHTMLAttributes,
} from "react";
import { createPortal } from "react-dom";
import { useT } from "../../store/useStore";
import { Icon } from "./Icon";

export function Spinner({ size = 16, color }: { size?: number; color?: string }) {
  return (
    <span
      className="spin"
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        border: `2px solid ${color || "var(--field-border)"}`,
        borderTopColor: color ? "transparent" : "var(--accent-text)",
        display: "inline-block",
        flex: "none",
      }}
    />
  );
}

export interface BtnProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "ghost" | "subtle" | "danger";
  icon?: string;
  iconWeight?: string;
  size?: "sm" | "md" | "lg";
}

export function Btn({ variant = "primary", icon, iconWeight, children, className = "", size = "md", style = {}, ...rest }: BtnProps) {
  const sizeStyle: CSSProperties =
    size === "sm" ? { padding: "7px 13px", fontSize: 13 } : size === "lg" ? { padding: "13px 24px", fontSize: 15 } : {};
  return (
    <button className={`btn btn-${variant} ring-focus ${className}`} style={{ ...sizeStyle, ...style }} {...rest}>
      {icon && <Icon name={icon} weight={iconWeight} size={size === "sm" ? 15 : 17} />}
      {children}
    </button>
  );
}

export interface IconBtnProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: string;
  weight?: string;
  size?: number;
  box?: number;
  active?: boolean;
}

export function IconBtn({ icon, weight, size = 18, box = 38, active, title, className = "", style = {}, ...rest }: IconBtnProps) {
  return (
    <button
      title={title}
      aria-label={title}
      className={`icon-btn ring-focus ${className}`}
      style={{
        width: box,
        height: box,
        background: active ? "var(--accent-soft)" : undefined,
        color: active ? "var(--accent-text)" : undefined,
        ...style,
      }}
      {...rest}
    >
      <Icon name={icon} weight={weight} size={size} />
    </button>
  );
}

export function Field({ label, hint, children, className = "" }: { label?: string; hint?: string; children: ReactNode; className?: string }) {
  return (
    <div className={className}>
      {label && <label className="lbl">{label}</label>}
      {children}
      {hint && <p style={{ fontSize: 12, color: "var(--text-mute)", marginTop: 6 }}>{hint}</p>}
    </div>
  );
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  icon?: string;
  mono?: boolean;
  /**
   * Seit React 19 ist `ref` eine gewoehnliche Prop einer Funktionskomponente —
   * forwardRef braucht es dafuer nicht mehr. Sie landet ueber ...rest ohnehin
   * am <input>; hier steht sie nur, damit der Typ das auch zulaesst. Ohne diese
   * Zeile scheitert jeder Aufrufer, der das Feld fokussieren will, an TS2322,
   * obwohl es zur Laufzeit funktioniert haette.
   */
  ref?: Ref<HTMLInputElement>;
}

export function Input({ icon, className = "", mono, style, ...rest }: InputProps) {
  if (icon)
    return (
      <div style={{ position: "relative" }}>
        <Icon
          name={icon}
          size={16}
          style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-mute)", pointerEvents: "none" }}
        />
        <input className={`inp ${mono ? "mono" : ""} ${className}`} style={{ paddingLeft: 36, ...style }} {...rest} />
      </div>
    );
  return <input className={`inp ${mono ? "mono" : ""} ${className}`} style={style} {...rest} />;
}

export type SelectOption = string | { value: string; label: string };

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "value" | "onChange"> {
  value: string | null | undefined;
  onChange: (e: ChangeEvent<HTMLSelectElement>) => void;
  options: SelectOption[];
  placeholder?: string;
  /** smaller trigger to sit inline in headers/toolbars */
  compact?: boolean;
  /** filter box at the top of the list; defaults to on for >12 options */
  searchable?: boolean;
}

// Custom popover dropdown — the native <select> open list is OS-drawn and can't
// be rounded/framed, so we render our own themed listbox (portal) that matches
// the rest of the app (light + dark). Drop-in: same props, onChange still fires
// a {target:{value}} shaped event so existing call sites keep working.
export function Select({ value, onChange, options, className = "", placeholder, disabled, style, compact, searchable }: SelectProps) {
  const t = useT();
  const norm = options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [pos, setPos] = useState<{ left: number; top: number; width: number; up: boolean } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const current = norm.find((o) => o.value === (value ?? ""));
  // long lists get a filter box automatically (field pickers etc.); short ones stay plain
  const withSearch = searchable ?? norm.length > 12;
  const needle = q.trim().toLowerCase();
  const shown = needle ? norm.filter((o) => `${o.label} ${o.value}`.toLowerCase().includes(needle)) : norm;

  const place = useCallback(() => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const maxH = Math.min(264, norm.length * 38 + 12) + (withSearch ? 46 : 0);
    const up = r.bottom + maxH + 8 > window.innerHeight && r.top - maxH - 8 > 0;
    setPos({ left: r.left, top: up ? r.top - 4 : r.bottom + 4, width: r.width, up });
  }, [norm.length, withSearch]);

  useLayoutEffect(() => {
    if (open) {
      setQ("");
      place();
    }
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    // close only on a click outside / Escape — scroll & resize just REPOSITION so the
    // list stays attached (like a native select) instead of snapping shut.
    const onDown = (e: Event) => {
      if (listRef.current?.contains(e.target as Node) || btnRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // die offene Liste konsumiert das Escape VOLLSTAENDIG — sonst schliesst
        // dasselbe Escape auch ein Modal darueber und wirft dessen Eingaben weg
        e.stopPropagation();
        // a non-empty filter eats the first Escape (clear), the second closes
        setQ((cur) => {
          if (cur) return "";
          setOpen(false);
          return cur;
        });
      }
    };
    const onScroll = (e: Event) => {
      if (listRef.current?.contains(e.target as Node)) return; // scrolling inside the list is fine
      place();
    };
    const onResize = () => place();
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    document.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open, place]);

  const choose = (v: string) => {
    onChange({ target: { value: v } } as unknown as ChangeEvent<HTMLSelectElement>);
    setOpen(false);
    btnRef.current?.focus();
  };

  return (
    <div style={{ position: "relative", ...style }}>
      <button
        ref={btnRef}
        type="button"
        disabled={disabled}
        className={`inp ${className}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => !disabled && setOpen((o) => !o)}
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, textAlign: "left", cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.55 : 1, ...(compact ? { padding: "6px 11px", fontSize: 13, borderRadius: 9 } : {}) }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: current ? "var(--text)" : "var(--text-mute)" }}>
          {current?.label ?? placeholder ?? ""}
        </span>
        <Icon name="caret-down" size={14} style={{ color: "var(--text-mute)", flex: "none", transition: "transform .15s", transform: open ? "rotate(180deg)" : "none" }} />
      </button>
      {open &&
        pos &&
        createPortal(
          <div
            ref={listRef}
            className="scroll anim-pop"
            role="listbox"
            style={{
              position: "fixed",
              left: pos.left,
              top: pos.up ? undefined : pos.top,
              bottom: pos.up ? window.innerHeight - pos.top : undefined,
              width: pos.width,
              maxHeight: 264,
              overflowY: "auto",
              borderRadius: 12,
              padding: 5,
              zIndex: 1000,
              background: "var(--panel-solid)",
              border: "1px solid var(--glass-border-strong)",
              boxShadow: "var(--shadow-lg)",
            }}
          >
            {withSearch && (
              <div style={{ position: "sticky", top: 0, zIndex: 1, padding: "1px 1px 6px", background: "var(--panel-solid)" }}>
                <Input
                  icon="magnifying-glass"
                  autoFocus
                  value={q}
                  placeholder={t("common.search")}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => {
                    // Enter on a unique match picks it straight away
                    if (e.key === "Enter" && shown.length === 1) choose(shown[0]!.value);
                  }}
                  style={{ padding: "7px 10px 7px 34px", fontSize: 13, borderRadius: 9 }}
                />
              </div>
            )}
            {withSearch && shown.length === 0 && (
              <div style={{ padding: "10px 12px", fontSize: 12.5, color: "var(--text-mute)" }}>{t("common.noMatches")}</div>
            )}
            {shown.map((o) => {
              const on = o.value === (value ?? "");
              return (
                <button
                  key={o.value}
                  type="button"
                  role="option"
                  aria-selected={on}
                  onClick={() => choose(o.value)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    width: "100%",
                    textAlign: "left",
                    padding: "8px 10px",
                    borderRadius: 8,
                    border: "none",
                    cursor: "pointer",
                    fontSize: 14,
                    fontFamily: "inherit",
                    fontWeight: on ? 600 : 500,
                    background: on ? "var(--accent-soft)" : "transparent",
                    color: on ? "var(--accent-text)" : "var(--text-dim)",
                  }}
                  onMouseEnter={(e) => {
                    if (!on) e.currentTarget.style.background = "var(--hover)";
                  }}
                  onMouseLeave={(e) => {
                    if (!on) e.currentTarget.style.background = "transparent";
                  }}
                >
                  {on ? <Icon name="check" size={13} style={{ flex: "none" }} /> : <span style={{ width: 13, flex: "none" }} />}
                  <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.label}</span>
                </button>
              );
            })}
          </div>,
          portalRoot(),
        )}
    </div>
  );
}

export function IOSwitch({ on, onChange, disabled }: { on: boolean; onChange?: (v: boolean) => void; disabled?: boolean }) {
  return (
    <div
      className={`io ${on ? "on" : ""}`}
      role="switch"
      aria-checked={!!on}
      onClick={() => !disabled && onChange && onChange(!on)}
      style={{ opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? "none" : "auto" }}
    >
      <div className="knob" />
    </div>
  );
}

export interface SliderProps {
  value: number;
  min?: number;
  max?: number;
  step?: number;
  onChange?: (v: number) => void;
  showVal?: boolean;
  suffix?: string;
  fmt?: (v: number) => string;
}

export function Slider({ value, min = 0, max = 100, step = 1, onChange, showVal = true, suffix = "", fmt }: SliderProps) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
      <input
        type="range"
        className="rng"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange && onChange(Number(e.target.value))}
        style={{ background: `linear-gradient(90deg, var(--accent) ${pct}%, var(--field-border) ${pct}%)` }}
      />
      {showVal && (
        <span className="font-mono tnum" style={{ fontSize: 13, minWidth: 34, textAlign: "right", color: "var(--text)", fontWeight: 600 }}>
          {fmt ? fmt(value) : value}
          {suffix}
        </span>
      )}
    </div>
  );
}

// Draggable circular dial
export interface DialProps {
  value: number;
  min?: number;
  max?: number;
  step?: number;
  onChange?: (v: number) => void;
  size?: number;
  unit?: string;
  /** Anzeige in der Mitte — ohne Angabe die gerundete Zahl (z. B. fuer das deutsche Dezimalkomma) */
  fmt?: (v: number) => string;
}

export function Dial({ value, min = 0, max = 100, step = 1, onChange, size = 86, unit = "", fmt }: DialProps) {
  const ref = useRef<HTMLDivElement>(null);
  const sweep = 270,
    start = 135; // degrees
  const frac = Math.max(0, Math.min(1, (value - min) / (max - min)));
  const angle = start + frac * sweep;
  const r = size / 2 - 9,
    cx = size / 2,
    cy = size / 2;
  const polar = (deg: number): [number, number] => {
    const a = ((deg - 90) * Math.PI) / 180;
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
  };
  const [sx, sy] = polar(start);
  const [ex, ey] = polar(angle);
  const large = angle - start > 180 ? 1 : 0;
  const [fex, fey] = polar(start + sweep);
  const trackLarge = sweep > 180 ? 1 : 0;
  const [hx, hy] = polar(angle);

  const handle = useCallback(
    (clientX: number, clientY: number) => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const dx = clientX - (rect.left + rect.width / 2);
      const dy = clientY - (rect.top + rect.height / 2);
      let deg = (Math.atan2(dy, dx) * 180) / Math.PI + 90; // 0 at top
      if (deg < 0) deg += 360;
      // map to start..start+sweep ; shift so range centers at bottom gap
      let rel = deg - start;
      if (rel < -30) rel += 360;
      rel = Math.max(0, Math.min(sweep, rel));
      let v = min + (rel / sweep) * (max - min);
      v = Math.round(v / step) * step;
      v = Math.max(min, Math.min(max, v));
      onChange && onChange(v);
    },
    [min, max, step, onChange],
  );

  const onDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    const move = (ev: PointerEvent) => handle(ev.clientX, ev.clientY);
    handle(e.clientX, e.clientY);
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  return (
    <div
      ref={ref}
      onPointerDown={onDown}
      style={{ width: size, height: size, position: "relative", cursor: "grab", touchAction: "none", userSelect: "none" }}
    >
      <svg width={size} height={size}>
        <path d={`M ${sx} ${sy} A ${r} ${r} 0 ${trackLarge} 1 ${fex} ${fey}`} className="dial-track" strokeWidth="6" fill="none" strokeLinecap="round" />
        <path d={`M ${sx} ${sy} A ${r} ${r} 0 ${large} 1 ${ex} ${ey}`} className="dial-fill" strokeWidth="6" fill="none" strokeLinecap="round" />
        <circle cx={hx} cy={hy} r="7" fill="var(--panel-solid)" stroke="var(--accent-text)" strokeWidth="3" />
      </svg>
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "none",
        }}
      >
        <span className="font-mono tnum" style={{ fontSize: size > 70 ? 18 : 15, fontWeight: 600 }}>
          {fmt ? fmt(value) : Math.round(value)}
        </span>
        {unit && <span style={{ fontSize: 10, color: "var(--text-mute)", marginTop: 1 }}>{unit}</span>}
      </div>
    </div>
  );
}

export interface TabDef {
  id: string;
  label: string;
  icon?: string;
}

export interface TabsProps {
  tabs: TabDef[];
  active: string;
  onChange: (id: string) => void;
  className?: string;
  variant?: "underline" | "pill";
  compact?: boolean;
}

export function Tabs({ tabs, active, onChange, className = "", variant = "underline", compact = false }: TabsProps) {
  if (variant === "pill")
    return (
      <div className={`seg ${className}`}>
        {tabs.map((t) => (
          <button key={t.id} className={active === t.id ? "active" : ""} onClick={() => onChange(t.id)}>
            {t.label}
          </button>
        ))}
      </div>
    );
  return (
    <div className={`flex ${className}`} style={{ display: "flex", flexWrap: "wrap", borderBottom: "1px solid var(--glass-border)", gap: 2 }}>
      {tabs.map((t) => {
        const on = active === t.id;
        return (
          <button
            key={t.id}
            onClick={() => onChange(t.id)}
            style={{
              position: "relative",
              padding: compact ? "11px 11px" : "11px 16px",
              fontSize: 13.5,
              fontWeight: 600,
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: on ? "var(--text)" : "var(--text-mute)",
              display: "flex",
              alignItems: "center",
              gap: 7,
              transition: "color .15s",
              whiteSpace: "nowrap",
            }}
          >
            {t.icon && <Icon name={t.icon} size={16} />}
            {t.label}
            {on && (
              <span style={{ position: "absolute", left: 8, right: 8, bottom: -1, height: 2, background: "var(--accent-text)", borderRadius: 2 }} />
            )}
          </button>
        );
      })}
    </div>
  );
}

export type ChipTone = "neutral" | "teal" | "good" | "warn" | "error";

const CHIP_TONES: Record<ChipTone, CSSProperties> = {
  neutral: { background: "var(--field)", color: "var(--text-dim)" },
  teal: { background: "var(--accent-soft)", color: "var(--accent-text)" },
  good: { background: "color-mix(in srgb, var(--good) 14%, transparent)", color: "var(--good)" },
  warn: { background: "color-mix(in srgb, var(--warn) 16%, transparent)", color: "var(--warn)" },
  error: { background: "var(--error-soft)", color: "var(--error)" },
};

export function Chip({
  children,
  tone = "neutral",
  icon,
  className = "",
  style = {},
}: {
  children: ReactNode;
  tone?: ChipTone;
  icon?: string;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <span className={`chip ${className}`} style={{ ...CHIP_TONES[tone], ...style }}>
      {icon && <Icon name={icon} size={12} weight="bold" />}
      {children}
    </span>
  );
}

export function Avatar({ initials, photo, size = 36, ring }: { initials: string; photo?: string | null; size?: number; ring?: boolean }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        flex: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: photo ? `center/cover url(${photo})` : "var(--accent)",
        color: "#fff",
        fontWeight: 700,
        fontSize: size * 0.36,
        boxShadow: ring ? "0 0 0 2px var(--bg), 0 0 0 4px var(--accent-ring)" : undefined,
        // Echtes CSS, also die Variable und nicht der blosse Familienname:
        // "Inter" allein faellt vor dem Laden auf die Browservorgabe zurueck
        // (meist Serife) statt auf die angeglichene Ersatzfamilie — bei
        // Initialen in einem runden Feld faellt genau das auf.
        fontFamily: "var(--schrift-text)",
      }}
    >
      {!photo && initials}
    </div>
  );
}

export function EmptyState({ icon, title, sub }: { icon: string; title: string; sub?: string }) {
  return (
    <div style={{ textAlign: "center", padding: "48px 20px", color: "var(--text-mute)" }}>
      <Icon name={icon} weight="light" size={46} style={{ opacity: 0.5 }} />
      <p style={{ fontWeight: 600, color: "var(--text-dim)", marginTop: 14 }}>{title}</p>
      {sub && <p style={{ fontSize: 13, marginTop: 5 }}>{sub}</p>}
    </div>
  );
}

export function portalRoot(): HTMLElement {
  return document.getElementById("root") || document.body;
}
